import React from "react";
import { ArrowUpRight, Info } from "lucide-react";
import { formatSignedPercentage } from "../../../utils/numberUtils";
import type { MoveBand, MoveContext } from "../../../utils/strategyMoveContext";

interface BenchmarkOption {
  conId: number;
  label: string;
}

interface Props {
  moveContext: MoveContext | null;
  isLoading: boolean;
  error: string | null;
  benchmarks: BenchmarkOption[];
  selectedConId: number;
  onSelectBenchmark: (conId: number) => void;
  selectedDate: string | null; // null = today
  onSelectDate: (date: string | null) => void;
  dateBounds: { min: string; max: string } | null;
  onExpand: () => void;
}

// Benchmark-relative reassurance copy ("what happened"), day-agnostic.
const REASSURANCE: Record<MoveBand, string> = {
  quiet: "Right in line with the benchmark.",
  normal: "A normal day versus the benchmark.",
  elevated: "Diverging from the benchmark more than usual.",
  large: "A large move versus the benchmark - rare.",
  insufficient: "Not enough overlapping history to compare to the benchmark.",
  flat: "Tracks the benchmark almost exactly - no normal gap to compare against.",
};

// Per-band takeaway ("what to do") -- the line that resolves the feeling.
const TAKEAWAY: Partial<Record<MoveBand, string>> = {
  quiet: "Nothing unusual - no action needed.",
  normal: "A normal day - no action needed.",
  elevated: "More movement than usual - still within reason.",
  large: "An unusually large gap - worth understanding why.",
};

// Plain-word status (sigma hidden in the headline -- decision 4).
function statusWord(band: MoveBand): string {
  switch (band) {
    case "elevated":
      return "above range";
    case "large":
      return "well outside";
    default:
      return "within range";
  }
}

function toneText(band: MoveBand): string {
  switch (band) {
    case "quiet":
      return "text-green-600 dark:text-green-500";
    case "elevated":
      return "text-rp-amber";
    case "large":
      return "text-red-600 dark:text-red-500";
    default:
      return "text-gray-600 dark:text-gray-300";
  }
}

function chipClasses(band: MoveBand): string {
  switch (band) {
    case "quiet":
      return "text-green-600 dark:text-green-500 border-green-600/40 dark:border-green-500/40 bg-green-50 dark:bg-green-900/20";
    case "elevated":
      return "text-rp-amber border-rp-amber/40 bg-rp-amber/10";
    case "large":
      return "text-red-600 dark:text-red-500 border-red-600/40 dark:border-red-500/40 bg-red-50 dark:bg-red-900/20";
    default:
      return "text-gray-600 dark:text-gray-500 border-gray-200 dark:border-rp-separator bg-gray-100 dark:bg-rp-surface-raised";
  }
}

function moveColor(value: number | null): string {
  if (value == null) return "text-gray-500 dark:text-gray-400";
  return value >= 0 ? "text-green-600 dark:text-green-500" : "text-red-600 dark:text-red-500";
}

function fmt(value: number | null, decimals = 2): string {
  return value != null ? formatSignedPercentage(value, decimals) : "--";
}

const CARD =
  "bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden";
const CONTROL =
  "bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-md text-xs text-gray-900 dark:text-white px-2 py-1 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all dark:[color-scheme:dark]";

function Header({
  benchmarks,
  selectedConId,
  onSelectBenchmark,
  selectedDate,
  onSelectDate,
  dateBounds,
}: Pick<
  Props,
  "benchmarks" | "selectedConId" | "onSelectBenchmark" | "selectedDate" | "onSelectDate" | "dateBounds"
>) {
  return (
    <div className="flex items-center justify-between gap-2 mb-2">
      <input
        type="date"
        value={selectedDate ?? dateBounds?.max ?? ""}
        min={dateBounds?.min}
        max={dateBounds?.max}
        aria-label="View date"
        onChange={(e) => {
          const value = e.target.value;
          onSelectDate(!value || value === dateBounds?.max ? null : value);
        }}
        className={CONTROL}
      />
      <div className="flex items-center gap-1.5">
        <div className="relative group">
          <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700 dark:hover:text-white cursor-help transition-colors" />
          <div className="absolute top-full right-0 mt-1.5 w-64 px-3 py-2.5 rounded-lg bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator text-xs leading-relaxed text-gray-600 dark:text-gray-300 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-30 shadow-lg">
            We compare your move to the benchmark, then to this strategy's own normal daily gap. "Within range" = a normal day. Open "see where today sits" for the detail.
          </div>
        </div>
        <select
          value={selectedConId}
          onChange={(e) => onSelectBenchmark(Number(e.target.value))}
          aria-label="Benchmark"
          className={CONTROL}
        >
          {benchmarks.map((b) => (
            <option key={b.conId} value={b.conId}>
              {b.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function Shell({ children, header }: { children: React.ReactNode; header: React.ReactNode }) {
  return (
    <div className={CARD}>
      <div className="p-4">
        {header}
        {children}
      </div>
    </div>
  );
}

export default function StrategyMoveContextCard({
  moveContext,
  isLoading,
  error,
  benchmarks,
  selectedConId,
  onSelectBenchmark,
  selectedDate,
  onSelectDate,
  dateBounds,
  onExpand,
}: Props) {
  const header = (
    <Header
      benchmarks={benchmarks}
      selectedConId={selectedConId}
      onSelectBenchmark={onSelectBenchmark}
      selectedDate={selectedDate}
      onSelectDate={onSelectDate}
      dateBounds={dateBounds}
    />
  );

  const isToday = !selectedDate || (dateBounds != null && selectedDate === dateBounds.max);
  const dayLabel = isToday ? "today" : (selectedDate as string);

  if (isLoading) {
    return (
      <Shell header={header}>
        <div className="space-y-3">
          <div className="flex items-center justify-between animate-pulse">
            <div className="h-6 w-24 rounded bg-gray-200 dark:bg-gray-800" />
            <div className="h-5 w-16 rounded-full bg-gray-200 dark:bg-gray-800" />
          </div>
          <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-800 animate-pulse" />
          <div className="h-2 w-full rounded bg-gray-200 dark:bg-gray-800 animate-pulse" />
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell header={header}>
        <p className="text-xs text-gray-500 dark:text-gray-400">Context unavailable</p>
      </Shell>
    );
  }

  if (!moveContext || moveContext.band === "insufficient") {
    return (
      <Shell header={header}>
        <p className="text-sm text-gray-600 dark:text-gray-300">{REASSURANCE.insufficient}</p>
      </Shell>
    );
  }

  const ctx = moveContext;
  const hasSigma = ctx.sigma != null;
  const hasBaseline = ctx.trackingErrorPct != null && ctx.trackingErrorPct > 0;
  const activeMoved = ctx.activeDayPct != null && Math.abs(ctx.activeDayPct) >= 0.005;

  let message: string;
  if (hasSigma) {
    message = REASSURANCE[ctx.band];
  } else if (!hasBaseline) {
    message = activeMoved
      ? "No prior divergence to scale against - the strategy is moving versus the benchmark."
      : REASSURANCE.flat;
  } else if (ctx.strategyDayPct == null) {
    message = `No data for ${dayLabel}.`;
  } else {
    message = isToday
      ? "Benchmark not in yet today. Typical gap is shown below."
      : `No benchmark data for ${dayLabel}.`;
  }
  const messageClass = hasSigma ? toneText(ctx.band) : "text-gray-500 dark:text-gray-400";
  const takeaway = hasSigma ? TAKEAWAY[ctx.band] : undefined;

  const markerPct =
    ctx.sigma != null ? Math.max(2, Math.min(98, ((ctx.sigma + 1) / 2) * 100)) : 50;

  return (
    <Shell header={header}>
      {/* Strategy / benchmark / active rows for the focus day */}
      <div className="space-y-1">
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">Strategy</span>
          <span className={`font-mono text-base font-bold ${moveColor(ctx.strategyDayPct)}`}>
            {fmt(ctx.strategyDayPct)}
          </span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">Benchmark</span>
          <span className={`font-mono text-sm ${moveColor(ctx.benchmarkDayPct)}`}>
            {fmt(ctx.benchmarkDayPct)}
          </span>
        </div>
        <div className="flex items-baseline justify-between border-t border-gray-100 dark:border-gray-800 pt-1.5 mt-1">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Gap</span>
          <span className="flex items-baseline gap-2">
            <span className={`font-mono text-base font-bold ${moveColor(ctx.activeDayPct)}`}>
              {fmt(ctx.activeDayPct)}
            </span>
            {hasSigma && (
              <span
                className={`text-xs font-semibold rounded-full border px-2 py-0.5 ${chipClasses(ctx.band)}`}
              >
                {statusWord(ctx.band)}
              </span>
            )}
          </span>
        </div>
      </div>

      <p className={`mt-2.5 text-sm leading-snug ${messageClass}`}>{message}</p>
      {takeaway && <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white leading-snug">{takeaway}</p>}

      {/* Typical active-gap range bar (+/-1 tracking error) */}
      {hasBaseline && (
        <div className="mt-3">
          <div className="relative h-2 rounded-full bg-gray-200 dark:bg-gray-800">
            <div className="absolute inset-y-0 left-1/4 right-1/4 rounded-full bg-green-100 dark:bg-green-900/30" />
            <div className="absolute -top-0.5 bottom-[-2px] left-1/2 w-px bg-gray-400 dark:bg-gray-600" />
            {ctx.sigma != null && (
              <div
                className={`absolute -top-1 h-4 w-[3px] rounded-full ${
                  ctx.activeDayPct != null && ctx.activeDayPct < 0 ? "bg-red-500" : "bg-green-500"
                }`}
                style={{ left: `${markerPct}%` }}
              />
            )}
          </div>
          <div className="mt-1.5 flex justify-between font-mono text-[10px] text-gray-500 dark:text-gray-400">
            <span>{fmt(ctx.bandLowPct, 1)}</span>
            <span className="text-gray-400 dark:text-gray-500">expected range</span>
            <span>{fmt(ctx.bandHighPct, 1)}</span>
          </div>
        </div>
      )}

      {ctx.percentile != null && (
        <p className="mt-2.5 text-xs text-gray-500 dark:text-gray-400">
          Bigger gap than{" "}
          <span className="text-gray-700 dark:text-gray-300">{Math.round(ctx.percentile)}%</span> of recent days
        </p>
      )}

      {hasBaseline && (
        <button
          type="button"
          onClick={onExpand}
          className="mt-3 flex w-full items-center justify-between border-t border-gray-100 dark:border-gray-800 pt-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          <span>See where {dayLabel === "today" ? "today" : dayLabel} sits</span>
          <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      )}
    </Shell>
  );
}
